import {
  formatDate,
  parseAutoCompleteValue,
  type IMetadata,
  type ICardSlot,
  type IField,
  type ILookup,
  type ICardStaticFieldBinding,
  type ISlotEntryData,
  type ISlotData,
  type ICardData,
} from '@moko/utils';
import { getFileNameFromUrl } from '../file';
import { getFileViewerUrl } from '../OtherInfo/fields/fileViewerUtils';

export enum FontSize {
  xs = 'xs',
  sm = 'sm',
  md = 'md',
  lg = 'lg',
}

const FONT_SIZE_MAP: Record<FontSize, string> = {
  [FontSize.xs]: '12px',
  [FontSize.sm]: '13px',
  [FontSize.md]: '16px',
  [FontSize.lg]: '20px',
};

const FONT_WEIGHT_MAP: Record<FontSize, string> = {
  [FontSize.xs]: '400',
  [FontSize.sm]: '500',
  [FontSize.md]: '600',
  [FontSize.lg]: '700',
};

const FIELD_ICON_MAP: Record<string, string> = {
  Date: 'CalendarRegular',
  Address: 'LocationRegular',
  Coordinate: 'MapRegular',
  FileViewer: 'DocumentRegular',
};

const STATIC_FIELD_LABELS: Record<string, string> = {
  id: 'Id',
  code: 'Code',
  name: 'Name',
  description: 'Description',
  status: 'Status',
  entityTypeName: 'Entity type name',
};

/**
 * Item field name to read for each `CardStaticFieldKey`. `status` is special
 * because the renderer needs the lookup id (not the human label) to resolve
 * the status's color, so we pull `statusId` and let the renderer decide.
 */
const STATIC_FIELD_SOURCE: Record<string, string> = {
  status: 'statusId',
  entityTypeName: 'entityTypeName',
};

export const getSortedSlots = (metadata: IMetadata): ICardSlot[] =>
  [...(metadata.cardLayout?.slots ?? [])].sort(
    (a, b) => (a.order ?? 0) - (b.order ?? 0)
  );

export const getFieldsForSlot = (fields: IField[], slotId: string): IField[] =>
  fields
    .filter((f) => f.cardSlotId === slotId)
    .sort(
      (a, b) =>
        (a.cardSlotOrder ?? Number.MAX_SAFE_INTEGER) -
        (b.cardSlotOrder ?? Number.MAX_SAFE_INTEGER)
    );

export const getStaticBindingsForSlot = (
  bindings: ICardStaticFieldBinding[],
  slotId: string,
  position: 'start' | 'end'
): ICardStaticFieldBinding[] =>
  bindings.filter((b) => b.slotId === slotId && b.position === position);

const resolveLookupDisplay = (
  lookups: ILookup[],
  lookupList: string | undefined,
  rawValue: string
): string => {
  if (!lookupList) return rawValue;
  const lookup = lookups.find((l) => l.name === lookupList);
  if (!lookup) return rawValue;
  const labels = new Map(lookup.values.map((v) => [String(v.key), v.value]));
  return rawValue
    .split('|')
    .filter(Boolean)
    .map((key) => labels.get(key) ?? key)
    .join(', ');
};

export const resolveFieldValue = (
  item: Record<string, unknown>,
  otherInfo: Record<string, unknown>,
  field: IField,
  lookups: ILookup[]
): string => {
  if (field.fieldType === 'FileViewer') {
    const url = getFileViewerUrl(field.options);
    return url ? getFileNameFromUrl(url) : '';
  }

  const raw = item[field.name] ?? otherInfo[field.name];
  if (raw === undefined || raw === null || raw === '') return '';

  if (field.fieldType === 'Date' || field.fieldType === 'DateTime') {
    const hasTime =
      field.fieldType === 'DateTime' ||
      (!!field.options &&
        typeof field.options === 'object' &&
        !Array.isArray(field.options) &&
        (field.options as { supportTimeInDateField?: unknown })
          .supportTimeInDateField === true);
    return formatDate(String(raw), hasTime);
  }

  if (field.fieldType === 'AutoComplete') {
    return parseAutoCompleteValue(raw)?.display ?? String(raw);
  }

  if (field.fieldType === 'Lookup' || field.fieldType === 'RadioButton') {
    return resolveLookupDisplay(lookups, field.lookupList, String(raw));
  }

  if (field.fieldType === 'Checkbox') {
    return raw === true || raw === 'true' ? 'Yes' : 'No';
  }

  if (field.fieldType === 'Contact') {
    const ext = item[`${field.name}_Ext`] ?? otherInfo[`${field.name}_Ext`];
    const phone = String(raw);
    if (ext === undefined || ext === null || ext === '') return phone;
    return `${phone} Ext ${String(ext)}`;
  }

  return String(raw);
};

export const getFieldIcon = (fieldType: string): string | undefined =>
  FIELD_ICON_MAP[fieldType];

export const resolveFontSize = (fontSize?: string): string =>
  FONT_SIZE_MAP[(fontSize ?? FontSize.md) as FontSize] ??
  FONT_SIZE_MAP[FontSize.md];

export const resolveFontWeight = (fontSize?: string): string =>
  FONT_WEIGHT_MAP[(fontSize ?? FontSize.md) as FontSize] ??
  FONT_WEIGHT_MAP[FontSize.md];

const parseOtherInfo = (otherInfo: unknown): Record<string, unknown> => {
  if (!otherInfo) return {};
  if (typeof otherInfo === 'object')
    return otherInfo as Record<string, unknown>;
  try {
    return JSON.parse(String(otherInfo));
  } catch {
    return {};
  }
};

export const resolveMetadataForItem = (
  metadataMap: Record<string, IMetadata>,
  entity: string,
  item: Record<string, unknown>
): IMetadata | undefined => {
  const name = item.metadataName as string | undefined;
  if (name && metadataMap[name]) return metadataMap[name];
  const prefix = `${entity}|`;
  const fallbackKey = Object.keys(metadataMap).find((k) =>
    k.startsWith(prefix)
  );
  return fallbackKey ? metadataMap[fallbackKey] : undefined;
};

/**
 * Default card shown when an item has no resolvable card metadata/layout.
 * Mirrors what the desktop grid always shows (code/description/status) so the
 * mobile list never silently drops rows just because metadata is missing.
 */
const buildDefaultCardData = (
  item: Record<string, unknown>,
  fallbackField?: string
): ICardData => {
  const itemId = (item.id as number) ?? 0;
  const candidates: { key: string; label: string; source: string }[] = [
    { key: 'code', label: STATIC_FIELD_LABELS.code, source: 'code' },
    {
      key: 'description',
      label: STATIC_FIELD_LABELS.description,
      source: 'description',
    },
    { key: 'name', label: STATIC_FIELD_LABELS.name, source: 'name' },
    { key: 'status', label: STATIC_FIELD_LABELS.status, source: 'status' },
  ];
  if (fallbackField) {
    candidates.unshift({
      key: 'fallback',
      label: fallbackField,
      source: fallbackField,
    });
  }
  const entries: ISlotEntryData[] = candidates
    .map((c) => ({
      key: c.key,
      label: c.label,
      value: String(item[c.source] ?? ''),
    }))
    .filter((e) => e.value !== '');

  return {
    id: itemId,
    slots:
      entries.length > 0
        ? [
            {
              slotId: 'default',
              label: '',
              display: 'block',
              align: 'left',
              fontSize: resolveFontSize(FontSize.md),
              fontWeight: resolveFontWeight(FontSize.md),
              entries,
            },
          ]
        : [],
  };
};

export const buildCardData = (
  metadata: IMetadata | undefined,
  item: Record<string, unknown>,
  fallbackField?: string,
  externalOtherInfo?: Record<string, unknown>
): ICardData => {
  if (!metadata) {
    return buildDefaultCardData(item, fallbackField);
  }
  const slots = getSortedSlots(metadata);
  const staticBindings = metadata.cardLayout?.staticFields ?? [];
  const otherInfo = externalOtherInfo ?? parseOtherInfo(item.otherInfo);
  const itemId = (item.id as number) ?? 0;

  const slotDataList: ISlotData[] = slots
    .map((slot): ISlotData => {
      const startBindings = getStaticBindingsForSlot(
        staticBindings,
        slot.id,
        'start'
      );
      const endBindings = getStaticBindingsForSlot(
        staticBindings,
        slot.id,
        'end'
      );
      const fields = getFieldsForSlot(metadata.fields, slot.id);

      const startEntries: ISlotEntryData[] = startBindings
        .map((b) => ({
          key: `static-${b.key}`,
          label: STATIC_FIELD_LABELS[b.key] ?? b.key,
          value: String(item[STATIC_FIELD_SOURCE[b.key] ?? b.key] ?? ''),
        }))
        .filter((e) => e.value !== '');

      const fieldEntries: ISlotEntryData[] = fields
        .map((f) => ({
          key: f.name,
          label: f.label || f.name,
          value: resolveFieldValue(item, otherInfo, f, metadata.lookups),
          fieldType: f.fieldType,
          icon: getFieldIcon(f.fieldType),
        }))
        .filter((e) => e.value !== '');

      const endEntries: ISlotEntryData[] = endBindings
        .map((b) => ({
          key: `static-${b.key}`,
          label: STATIC_FIELD_LABELS[b.key] ?? b.key,
          value: String(item[STATIC_FIELD_SOURCE[b.key] ?? b.key] ?? ''),
        }))
        .filter((e) => e.value !== '');

      return {
        slotId: slot.id,
        label: slot.label,
        display: slot.style?.display ?? 'block',
        align: slot.style?.align ?? 'left',
        fontSize: resolveFontSize(slot.style?.fontSize),
        fontWeight: resolveFontWeight(slot.style?.fontSize),
        color: slot.style?.color,
        entries: [...startEntries, ...fieldEntries, ...endEntries],
      };
    })
    .filter((s) => s.entries.length > 0);

  // Metadata produced no visible content (e.g. empty fields / no card layout):
  // fall back to the default card so the item still renders.
  if (slotDataList.length === 0) {
    return buildDefaultCardData(item, fallbackField);
  }

  return { id: itemId, slots: slotDataList };
};
